import React, { useState, useEffect } from "react";
import { UserPlus, Trash2, Edit, Shield, Building2 } from "lucide-react";
import apiService from "../services/api";
import { useAuth } from "../context/AuthContext";
import "../styles/Users.css";

const Users = () => {
  const { user: currentUser } = useAuth();
  const [users, setUsers] = useState([]);
  const [customers, setCustomers] = useState([]);
  const [loading, setLoading] = useState(false);
  const [showAddModal, setShowAddModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showCustomersModal, setShowCustomersModal] = useState(false);
  const [editingUser, setEditingUser] = useState(null);
  const [managingCustomersUser, setManagingCustomersUser] = useState(null);
  const [userCustomers, setUserCustomers] = useState([]);
  const [selectedCustomers, setSelectedCustomers] = useState([]);
  const [newUser, setNewUser] = useState({
    username: "",
    password: "",
    permission: "viewer",
  });
  const [editUser, setEditUser] = useState({
    username: "",
    password: "",
    permission: "",
  });
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");

  useEffect(() => {
    loadUsers();
    loadCustomers();
  }, []);

  const loadUsers = async () => {
    try {
      setLoading(true);
      const response = await apiService.getUsers();
      if (response.success) {
        setUsers(response.users || []);
      }
    } catch (error) {
      console.error("Failed to load users:", error);
    } finally {
      setLoading(false);
    }
  };

  const loadCustomers = async () => {
    try {
      const response = await apiService.getCustomers();
      if (response.success) {
        setCustomers(response.customers || []);
      }
    } catch (error) {
      console.error("Failed to load customers:", error);
    }
  };

  const handleAddUser = async (e) => {
    e.preventDefault();
    setError("");
    setSuccess("");

    try {
      const response = await apiService.createUser(newUser);
      if (response.success) {
        setSuccess("משתמש נוסף בהצלחה!");
        setNewUser({ username: "", password: "", permission: "viewer" });
        setShowAddModal(false);
        loadUsers();
        setTimeout(() => setSuccess(""), 3000);
      } else {
        setError(response.error || "שגיאה ביצירת משתמש");
      }
    } catch (error) {
      setError(error.response?.data?.error || "שגיאה ביצירת משתמש");
    }
  };

  const handleEditUser = (user) => {
    setEditingUser(user);
    setEditUser({
      username: user.username,
      password: "",
      permission: user.permission,
    });
    setShowEditModal(true);
    setError("");
  };

  const handleUpdateUser = async (e) => {
    e.preventDefault();
    setError("");
    setSuccess("");

    try {
      const updateData = {
        username: editUser.username,
        permission: editUser.permission,
      };

      // Only include password if it was changed
      if (editUser.password) {
        updateData.password = editUser.password;
      }

      const response = await apiService.updateUser(editingUser.id, updateData);
      if (response.success) {
        setSuccess("משתמש עודכן בהצלחה!");
        setShowEditModal(false);
        setEditingUser(null);
        loadUsers();
        setTimeout(() => setSuccess(""), 3000);
      } else {
        setError(response.error || "שגיאה בעדכון משתמש");
      }
    } catch (error) {
      setError(error.response?.data?.error || "שגיאה בעדכון משתמש");
    }
  };

  const handleDeleteUser = async (userId, username) => {
    if (!window.confirm(`האם למחוק את המשתמש "${username}"?`)) {
      return;
    }

    try {
      const response = await apiService.deleteUser(userId);
      if (response.success) {
        setSuccess("משתמש נמחק בהצלחה!");
        loadUsers();
        setTimeout(() => setSuccess(""), 3000);
      } else {
        setError(response.error || "שגיאה במחיקת משתמש");
      }
    } catch (error) {
      setError(error.response?.data?.error || "שגיאה במחיקת משתמש");
    }
  };

  const handleManageCustomers = async (user) => {
    setManagingCustomersUser(user);
    setError("");

    try {
      // Load user's assigned customers
      const response = await apiService.getUserCustomers(user.id);
      if (response.success) {
        setSelectedCustomers(response.customer_ids || []);
      }
      setShowCustomersModal(true);
    } catch (error) {
      console.error("Failed to load user customers:", error);
      setError("שגיאה בטעינת לקוחות");
    }
  };

  const handleSaveCustomers = async () => {
    if (!managingCustomersUser) return;

    setError("");
    setSuccess("");

    try {
      const response = await apiService.assignCustomersToUser(
        managingCustomersUser.id,
        selectedCustomers
      );

      if (response.success) {
        setSuccess("לקוחות עודכנו בהצלחה!");
        setShowCustomersModal(false);
        setManagingCustomersUser(null);
        setTimeout(() => setSuccess(""), 3000);
      } else {
        setError(response.error || "שגיאה בעדכון לקוחות");
      }
    } catch (error) {
      setError(error.response?.data?.error || "שגיאה בעדכון לקוחות");
    }
  };

  const toggleCustomer = (customerId) => {
    setSelectedCustomers((prev) => {
      if (prev.includes(customerId)) {
        return prev.filter((id) => id !== customerId);
      } else {
        return [...prev, customerId];
      }
    });
  };

  const canModifyUser = (user) => {
    // Owner can modify anyone
    if (currentUser?.permission === "owner") return true;

    // Admin cannot modify owner accounts
    if (currentUser?.permission === "admin" && user.permission === "owner")
      return false;

    // Admin can modify others
    if (currentUser?.permission === "admin") return true;

    return false;
  };

  const needsCustomerAssignment = (user) => {
    // Only viewer and site_admin need customer assignments
    return user.permission === "viewer" || user.permission === "site_admin";
  };

  const getPermissionLabel = (permission) => {
    const labels = {
      viewer: "צופה",
      site_admin: "מנהל אתר",
      admin: "מנהל",
      owner: "בעלים",
    };
    return labels[permission] || permission;
  };

  const getPermissionColor = (permission) => {
    const colors = {
      viewer: "#94a3b8",
      site_admin: "#3b82f6",
      admin: "#f59e0b",
      owner: "#ef4444",
    };
    return colors[permission] || "#64748b";
  };

  return (
    <div className='users-container'>
      <div className='users-header'>
        <div>
          <h1>ניהול משתמשים</h1>
          <p>הוספה ועריכת משתמשי המערכת</p>
        </div>
        <button className='add-user-btn' onClick={() => setShowAddModal(true)}>
          <UserPlus size={18} />
          הוסף משתמש
        </button>
      </div>

      {success && <div className='success-banner'>{success}</div>}
      {error && <div className='error-banner'>{error}</div>}

      {loading ? (
        <div className='loading-container'>
          <div className='spinner'></div>
          <p>טוען משתמשים...</p>
        </div>
      ) : (
        <div className='users-table-container'>
          <table className='users-table'>
            <thead>
              <tr>
                <th>שם משתמש</th>
                <th>הרשאה</th>
                <th>פעולות</th>
              </tr>
            </thead>
            <tbody>
              {users.map((user) => (
                <tr key={user.id}>
                  <td className='username-cell'>{user.username}</td>
                  <td>
                    <span
                      className='permission-badge'
                      style={{
                        background: `${getPermissionColor(user.permission)}20`,
                        color: getPermissionColor(user.permission),
                      }}
                    >
                      <Shield size={14} />
                      {getPermissionLabel(user.permission)}
                    </span>
                  </td>
                  <td className='actions-cell'>
                    {canModifyUser(user) && (
                      <>
                        {needsCustomerAssignment(user) && (
                          <button
                            className='customers-btn'
                            onClick={() => handleManageCustomers(user)}
                            title='נהל לקוחות'
                          >
                            <Building2 size={16} />
                          </button>
                        )}
                        <button
                          className='edit-btn'
                          onClick={() => handleEditUser(user)}
                          title='ערוך משתמש'
                        >
                          <Edit size={16} />
                        </button>
                        <button
                          className='delete-btn'
                          onClick={() =>
                            handleDeleteUser(user.id, user.username)
                          }
                          title='מחק משתמש'
                        >
                          <Trash2 size={16} />
                        </button>
                      </>
                    )}
                    {!canModifyUser(user) && (
                      <span className='locked-badge' title='אין הרשאה לעריכה'>
                        <Shield size={14} />
                      </span>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Add User Modal */}
      {showAddModal && (
        <div className='modal-overlay' onClick={() => setShowAddModal(false)}>
          <div className='modal-content' onClick={(e) => e.stopPropagation()}>
            <h2>הוסף משתמש חדש</h2>
            <form onSubmit={handleAddUser}>
              <div className='form-group'>
                <label>שם משתמש</label>
                <input
                  type='text'
                  value={newUser.username}
                  onChange={(e) =>
                    setNewUser({ ...newUser, username: e.target.value })
                  }
                  required
                  placeholder='הזן שם משתמש'
                />
              </div>
              <div className='form-group'>
                <label>סיסמה</label>
                <input
                  type='password'
                  value={newUser.password}
                  onChange={(e) =>
                    setNewUser({ ...newUser, password: e.target.value })
                  }
                  required
                  minLength='6'
                  placeholder='הזן סיסמה (לפחות 6 תווים)'
                />
              </div>
              <div className='form-group'>
                <label>הרשאה</label>
                <select
                  value={newUser.permission}
                  onChange={(e) =>
                    setNewUser({ ...newUser, permission: e.target.value })
                  }
                  required
                >
                  <option value='viewer'>צופה</option>
                  <option value='recruiter'>מגייס</option>
                  <option value='payer'>משלם</option>
                  <option value='site_admin'>מנהל אתר</option>
                  <option value='admin'>מנהל</option>
                  <option value='owner'>בעלים</option>
                </select>
              </div>
              <div className='modal-actions'>
                <button
                  type='button'
                  className='cancel-btn'
                  onClick={() => setShowAddModal(false)}
                >
                  ביטול
                </button>
                <button type='submit' className='submit-btn'>
                  <UserPlus size={18} />
                  הוסף משתמש
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Edit User Modal */}
      {showEditModal && editingUser && (
        <div className='modal-overlay' onClick={() => setShowEditModal(false)}>
          <div className='modal-content' onClick={(e) => e.stopPropagation()}>
            <h2>ערוך משתמש</h2>
            <form onSubmit={handleUpdateUser}>
              <div className='form-group'>
                <label>שם משתמש</label>
                <input
                  type='text'
                  value={editUser.username}
                  onChange={(e) =>
                    setEditUser({ ...editUser, username: e.target.value })
                  }
                  required
                  placeholder='הזן שם משתמש'
                />
              </div>
              <div className='form-group'>
                <label>סיסמה חדשה (אופציונלי)</label>
                <input
                  type='password'
                  value={editUser.password}
                  onChange={(e) =>
                    setEditUser({ ...editUser, password: e.target.value })
                  }
                  minLength='6'
                  placeholder='השאר ריק אם לא רוצה לשנות'
                />
              </div>
              <div className='form-group'>
                <label>הרשאה</label>
                <select
                  value={editUser.permission}
                  onChange={(e) =>
                    setEditUser({ ...editUser, permission: e.target.value })
                  }
                  required
                >
                  <option value='viewer'>צופה</option>
                  <option value='recruiter'>מגייס</option>
                  <option value='payer'>משלם</option>
                  <option value='site_admin'>מנהל אתר</option>
                  <option value='admin'>מנהל</option>
                  {currentUser?.permission === "owner" && (
                    <option value='owner'>בעלים</option>
                  )}
                </select>
              </div>
              {error && <div className='error-message'>{error}</div>}
              <div className='modal-actions'>
                <button
                  type='button'
                  className='cancel-btn'
                  onClick={() => setShowEditModal(false)}
                >
                  ביטול
                </button>
                <button type='submit' className='submit-btn'>
                  <Edit size={18} />
                  עדכן משתמש
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Manage Customers Modal */}
      {showCustomersModal && managingCustomersUser && (
        <div
          className='modal-overlay'
          onClick={() => setShowCustomersModal(false)}
        >
          <div
            className='modal-content customers-modal'
            onClick={(e) => e.stopPropagation()}
          >
            <h2>נהל לקוחות - {managingCustomersUser.username}</h2>
            <p className='modal-subtitle'>בחר את הלקוחות שהמשתמש יוכל לראות</p>

            <div className='customers-list'>
              {customers.length === 0 ? (
                <p className='no-customers'>אין לקוחות זמינים</p>
              ) : (
                customers.map((customer) => (
                  <label key={customer.id} className='customer-checkbox'>
                    <input
                      type='checkbox'
                      checked={selectedCustomers.includes(customer.id)}
                      onChange={() => toggleCustomer(customer.id)}
                    />
                    <div className='customer-info'>
                      <span className='customer-name'>{customer.name}</span>
                      {customer.site_name && (
                        <span className='customer-site'>
                          {customer.site_name}
                        </span>
                      )}
                    </div>
                  </label>
                ))
              )}
            </div>

            {error && <div className='error-message'>{error}</div>}

            <div className='modal-actions'>
              <button
                type='button'
                className='cancel-btn'
                onClick={() => setShowCustomersModal(false)}
              >
                ביטול
              </button>
              <button
                type='button'
                className='submit-btn'
                onClick={handleSaveCustomers}
              >
                <Building2 size={18} />
                שמור לקוחות ({selectedCustomers.length})
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Users;
